import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { CardFooter } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertCircle } from "lucide-react";
import { useProfile } from "@/contexts/ProfileContext";

interface JobApplyButtonProps {
  jobId: string;
  isActive?: boolean;
}

export const JobApplyButton = ({ jobId, isActive }: JobApplyButtonProps) => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(false);
  const [isTestDeactivated, setIsTestDeactivated] = useState(false);
  const [inProgressTest, setInProgressTest] = useState<{
    id: string;
    last_question_index: number;
  } | null>(null);

  useEffect(() => {
    if (isActive === false) {
      setIsTestDeactivated(true);
    }
  }, [isActive]);

  // Check for in-progress test on mount
  useEffect(() => {
    const checkInProgressTest = async () => {
      if (!user || !jobId) return;
      
      const { data: inProgressResponse } = await supabase
        .from('test_responses')
        .select('id, last_question_index')
        .eq('test_id', jobId)
        .eq('candidate_id', user.id)
        .eq('status', 'in_progress')
        .maybeSingle();
      
      if (inProgressResponse) {
        setInProgressTest({
          id: inProgressResponse.id,
          last_question_index: inProgressResponse.last_question_index || 0
        });
      }
    };
    
    checkInProgressTest();
  }, [user, jobId]);

  if (profile?.role === 'employer' || profile?.role === 'administrator') {
    return null;
  }

  const handleTakeTest = async () => {
    if (!user) {
      toast({
        title: "Please login to take the test",
        description: "Sign in to continue with the assessment",
        className: "bg-card text-foreground",
      });
      
      navigate("/login", { 
        state: { returnTo: location.pathname }
      });
      return;
    }

    setIsChecking(true);
    
    try {
      const { data: test, error: testError } = await supabase
        .from('tests')
        .select('is_active, invitation_only')
        .eq('id', jobId)
        .single();
        
      if (testError) throw testError;
      
      if (!test.is_active) {
        setIsTestDeactivated(true);
        toast({
          title: "Test Unavailable",
          description: "This test is currently not accepting submissions.",
          variant: "destructive",
        });
        return;
      }

      // Check if test is invitation-only
      if (test.invitation_only) {
        const { data: invitation, error: inviteError } = await supabase
          .from('test_invitations')
          .select('id, email')
          .eq('test_id', jobId)
          .eq('email', user.email)
          .maybeSingle();

        if (inviteError && inviteError.code !== 'PGRST116') {
          console.error('Error checking invitation:', inviteError);
          throw inviteError;
        }

        if (!invitation) {
          toast({
            title: "Ineligible for Test",
            description: "You are not eligible to take this test. Please contact your test administrator for more details.",
            variant: "destructive",
          });
          return;
        }
      }

      // First check if user is creator or collaborator
      const [testCreatorQuery, collaboratorQuery] = await Promise.all([
        supabase
          .from('tests')
          .select('creator_id')
          .eq('id', jobId)
          .single(),
        supabase
          .from('test_collaborators')
          .select('id')
          .eq('test_id', jobId)
          .eq('collaborator_id', user.id)
          .eq('status', 'accepted')
          .maybeSingle()
      ]);

      const isCreator = testCreatorQuery.data?.creator_id === user.id;
      const isCollaborator = !!collaboratorQuery.data;
      
      // If user is creator or collaborator, they can take the test unlimited times
      if (!isCreator && !isCollaborator) {
        // For regular candidates, check for previous completed attempts
        const { data: previousResponses, error } = await supabase
          .from('test_responses')
          .select('id, status')
          .eq('test_id', jobId)
          .eq('candidate_id', user.id)
          .eq('status', 'completed');
          
        if (error) throw error;
        
        if (previousResponses && previousResponses.length > 0) {
          toast({
            title: "Test Already Completed",
            description: "You have already taken this test. You cannot take it again.",
            variant: "destructive",
          });
          return;
        }
      }
      
      const { data: assignment, error: assignmentError } = await supabase
        .from('test_assignments')
        .select('id')
        .eq('test_id', jobId)
        .eq('candidate_id', user.id)
        .single();
        
      if (assignmentError && assignmentError.code !== 'PGRST116') {
        console.error('Error checking assignment:', assignmentError);
        throw assignmentError;
      }
      
      if (!assignment) {
        const { error: createError } = await supabase
          .from('test_assignments')
          .insert({
            test_id: jobId,
            candidate_id: user.id,
            status: 'pending'
          });
          
        if (createError) throw createError;
      }
      
      navigate(`/test/${jobId}/take`);
    } catch (error: any) {
      console.error('Error checking previous attempts:', error);
      toast({
        title: "Error",
        description: "There was an error checking your previous attempts.",
        variant: "destructive",
      });
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <CardFooter className="bg-card flex flex-col gap-4">
      {isTestDeactivated && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This test is currently not accepting submissions. The employer has temporarily disabled this assessment.
          </AlertDescription>
        </Alert>
      )}
      <Button 
        onClick={handleTakeTest} 
        className="w-full rounded-sm"
        disabled={isChecking || isTestDeactivated}
      >
        {isChecking ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Checking...
          </>
        ) : isTestDeactivated ? (
          "Test Not Available"
        ) : inProgressTest ? (
          `Resume Test (Question ${inProgressTest.last_question_index + 1})`
        ) : (
          "Start Assessment"
        )}
      </Button>
    </CardFooter>
  );
};
